import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { getExemptions, setSearchExemptionPopover } from "../output.slice";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import { SearchType } from "Dashboard/domain/SearchType";
import { IExemptionSearch } from "Dashboard/scenes/Consumables/domain/IExemptionSearch";
import classesPopover from "../../../../.././../components/Popover/popover.module.scss";
import classesConsumables from "../../../popover.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import CategorySelect from "../../Input/components/CategorySelect";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import PartnerSelectForSearch from "Dashboard/scenes/Partners/components/PartnerSelectForSearch";
import UnitSelect from "Dashboard/components/UnitSelect";
import ResourceMaintenanceTypeSelect from "./ResourceMaintenanceTypeSelect";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IExemptionSearch>;
  register: UseFormRegister<IExemptionSearch>;
  control: Control<IExemptionSearch, any>;
  getValues: UseFormGetValues<IExemptionSearch>;
  reset: () => void;
  setFocus: UseFormSetFocus<IExemptionSearch>
  searchTypeValue: SearchType;
  setSearchTypeValue: React.Dispatch<React.SetStateAction<SearchType>>;
  setValue: UseFormSetValue<IExemptionSearch>;
  setGetOutputListItemsSearchParams: Dispatch<SetStateAction<IExemptionSearch>>;
}

const SearchExemptionPopover = (props: IProps) => {
  const dispatch = useAppDispatch();

  const { register, handleSubmit, control, getValues, setFocus, setValue, setGetOutputListItemsSearchParams }
    = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const close = () => {
    dispatch(setSearchExemptionPopover({ isOpen: false }));
  };

  const onSubmit = async (data: IExemptionSearch) => {
    try {
      if (props.searchTypeValue === SearchType.BASIC) {
        data = { searchTerm: data.searchTerm };
      } else {
        data.searchTerm = undefined;
      }
      setGetOutputListItemsSearchParams(data);
      close();
    } catch (err) {
      console.error(err);
    }
  };

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchTypeValue(searchType);
  };

  useEffect(() => {
    if (props.searchTypeValue === SearchType.BASIC) {
      setFocus("searchTerm");
    } else {
      setFocus("inputBillOfLandingId");
    }
  }, [setFocus, props.searchTypeValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className={`
        ${classesConsumables.searchInputPopover}
        ${classesPopover.basicPopoverView}
        ${props.searchTypeValue === SearchType.ADVANCED
          ? classesPopover.gPopoverInputs : ""}
      `}
    >
      {props.searchTypeValue === SearchType.BASIC && (
        <>
          <div>
            <div>
              <input type="text"
                className={`${classesPopover.fullWidth} ${classesPopover.solidBorder}`}
                {...register("searchTerm")}
              />
            </div>
            <div className={classesPopover.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classesPopover.buttons}>
            <label
              onClick={() => switchSearchMode(SearchType.ADVANCED)}
              className={classesPopover.clickableText}
            >
              NAPREDNA PRETRAGA
            </label>
            <button className={classesPopover.iconButton} type="submit">
              <FontAwesomeIcon
                icon={faLongArrowAltDown}
                className={classesPopover.icon} />
            </button>
          </div>
        </>
      )}
      {props.searchTypeValue === SearchType.ADVANCED && (
        <>
          <div>
            <div>
              <label>br. otpr. rač</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("inputBillOfLandingId")} />
            </div>
          </div>
          <div>
            <div>
              <label>br. otpremnice</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("outputBillOfLandingIdRange")} />
            </div>
          </div>
          <div>
            <div>
              <label>br. trebovanja</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("outputRequisitionIdRange")} />
            </div>
          </div>
          <div>
            <div>
              <label>naziv</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("name")} />
            </div>
          </div>
          <div>
            <div>
              <label>kategorija</label>
              <div className={classesConsumables.dropdownContainer}>
                <Controller
                  name="categories"
                  control={control}
                  defaultValue={undefined}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { onChange } }) => (
                    <CategorySelect
                      value={getValues("categories")}
                      onChange={onChange}
                      customStyles={styles.customSolidBorder}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div>
            <div>
              <label>dobavljač</label>
              <Controller
                name="supplier"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <PartnerSelectForSearch
                    value={getValues("supplier")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div>
            <div>
              <label>količina</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("quantityAmountRange", { required: false })} />
            </div>
          </div>

          <div>
            <div>
              <label>mera</label>
              <Controller
                name="quantityUnit"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <UnitSelect
                    value={getValues("quantityUnit")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div>
            <div>
              <label>cena izdatog</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("priceRange", { required: false })} />
            </div>
          </div>

          <div>
            <div>
              <label>ime resursa</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("resourceName", { required: false })} />
            </div>
          </div>

          <div>
            <div>
              <label>tablica resursa</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("resourceRegistrationPlate", { required: false })} />
            </div>
          </div>

          <div>
            <div>
              <label>usluga</label>
              <div className={classesConsumables.dropdownContainer}>
                <Controller
                  name="maintenanceTypes"
                  control={control}
                  defaultValue={undefined}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { onChange } }) => (
                    <ResourceMaintenanceTypeSelect
                      value={getValues("maintenanceTypes")}
                      onChange={onChange}
                      customStyles={styles.customSolidBorder}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div>
            <div>
              <label>datum izlaza</label>
              <div className={classesConsumables.calendarContainer}>
                <div className={`${classesPopover.dateWrapper} datepickerWrapper`}>
                  <DatePicker
                    selectsRange={true}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    showMonthDropdown
                    showYearDropdown
                    onChange={(date: any) => {
                      setDateRange([date[0], date[1]]);
                      setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                      setValue("dateTo", date[1] && FormatDateIso(date[1]));
                    }}
                    isClearable={true}
                    className={classesPopover.solidBorder}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <div>
              <label>napomena</label>
              <input type="text" className={`${classesConsumables.input} ${classesPopover.solidBorder}`}
                {...register("note", { required: false })} />
            </div>
          </div>

          <div className={classesPopover.buttons}>
            <label
              onClick={() => switchSearchMode(SearchType.BASIC)}
              className={classesPopover.clickableText}
            >
              PAMETNA PRETRAGA
            </label>
            <button className={classesPopover.iconButton} type="submit">
              <FontAwesomeIcon
                icon={faLongArrowAltDown}
                className={classesPopover.icon} />
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchExemptionPopover;
